import { createMongoAbility } from '@casl/ability'
import { CommonErrors, GeofencingMode, UserRole } from '@lmc/ts-common'
import * as Sentry from '@sentry/react'
import { initialiseBirdie } from 'common/services/birdie'
import * as GlobalActions from '../actions/Global'
import {
  initialiseAnalytics,
  initialiseGtm
} from '../common/services/analytics'
import { getJSON, postJSON, setFirstLogin } from '../common/services/api'
import initialiseFirebase from '../common/services/firebase'
import { initialiseIntercom } from '../common/services/intercom'
import { deferIdle } from '../common/utils/deferIdle'
import { history } from '../store'
import { initialiseListData } from './Data'

export function getHomeDetails(id) {
  return (dispatch) => {
    const url = `/api/list/homes/${id}`
    return getJSON({ url })
      .then((res) => {
        dispatch(GlobalActions.setHomeDetails(res.result))
      })
      .catch((err) => {
        console.error(err)
      })
  }
}

export function saveHomeDetails(homeDetails) {
  return (dispatch) => {
    const url = `list/homes/${homeDetails.id}`
    return postJSON({ url, json: homeDetails, method: 'PUT' })
      .then((res) => {
        dispatch(GlobalActions.setHomeDetails(res.result))
        // Return the result so callers can tell a persisted save from a
        // swallowed failure below (existing callers ignore the return value).
        return res
      })
      .catch((err) => {
        console.error(err)

        if (err?.code === CommonErrors.Codes.noSafeguardingOfficer) {
          throw err
        }
        // Non-safeguarding failures are swallowed for legacy callers; signal
        // failure to callers that check the return value (returns undefined).
      })
  }
}

export function disableGeofencing(homeId) {
  return (dispatch) =>
    postJSON({
      method: 'PUT',
      url: `list/homes/${homeId}/rota`,
      json: { geofencing: false, geofencingMode: GeofencingMode.Off }
    })
      .then((res) => {
        if (res?.data) {
          dispatch(GlobalActions.setHomeDetails(res.data))
        }
      })
      .catch((err) => {
        console.error('Failed to disable geofencing', err)
      })
}

// Fetch ENV variables, permissions and feature toggles from the backend
export function initialiseAppSession(initResponse, skipAnalytics = false) {
  return async (dispatch) => {
    try {
      const res = initResponse

      const {
        CHARGEBEE_ENV,
        MIXPANEL_TOKEN,
        GA_DOMAIN,
        GA_PROPERTY,
        GTM_ID, // 'GTM-T8QZF3C'
        INTERCOM_APP_ID,
        INTERCOM_HASH,
        FIREBASE_CONFIG,
        FIREBASE_PUBLIC_VAPID_KEY,
        user,
        home,
        homeGroup,
        permissionTemplates
      } = res

      // Mixpanel stays eager: the analytics layer does not buffer pre-init
      // calls, so deferring it would drop early route events. CM-3854.
      initialiseAnalytics({
        MIXPANEL_TOKEN,
        user,
        home,
        GA_DOMAIN,
        GA_PROPERTY
      })

      // GTM is the heavy, main-thread-blocking one and fires its own pageview
      // on load, so defer it off the boot tick. CM-3854.
      deferIdle(() => initialiseGtm(GTM_ID))

      Sentry.setUser({
        id: user.id,
        ip_address: '{{auto}}',
        other: {
          home: user.home,
          role: user.role
        }
      })

      dispatch(GlobalActions.setGlobalUser({ ...user, privacyOfficer: false }))
      dispatch(GlobalActions.setHomeDetails(home))
      dispatch(GlobalActions.setHomeGroup(homeGroup))
      dispatch(GlobalActions.setPermissionTemplates(permissionTemplates))
      dispatch(initialiseListData(user.role)) // Loads all list data

      if (
        home?.features?.pro?.enabled &&
        [UserRole.careHomeAdmin, UserRole.management].includes(user.role)
      ) {
        await initialiseFirebase({
          FIREBASE_CONFIG,
          FIREBASE_PUBLIC_VAPID_KEY
        })
      }

      if (skipAnalytics) {
        return
      }

      // TODO: import as npm module
      if (global.Chargebee !== undefined && !global.Chargebee.inited) {
        global.Chargebee.init({
          site: CHARGEBEE_ENV //  isProduction ? 'logmycare' : 'logmycare-test',
        })
      }

      // Check for, then set, first login
      if (!user.firstLogin) {
        setFirstLogin()
      }
      const isOnBoardingScreen =
        history.location.pathname.startsWith('/onboarding')
      if (!isOnBoardingScreen) {
        // Defer support-widget init (Intercom + Birdie) off the boot tick;
        // useIntercomReady polls for readiness, so a short delay is safe. CM-3854.
        // Scheduled separately so a throw in one (e.g. Intercom blocked by an
        // ad-blocker) can't stop the other from initialising.
        deferIdle(() =>
          initialiseIntercom({ INTERCOM_APP_ID, INTERCOM_HASH, user, home })
        )
        deferIdle(() =>
          initialiseBirdie(user?.name?.first, user?.email, INTERCOM_APP_ID)
        )
      }
    } catch (err) {
      console.error(err)
    }
  }
}

export function initialisePermissions() {
  return async (dispatch) => {
    try {
      dispatch(GlobalActions.setPermissionsLoading(true))

      const resAbility = await getJSON({ url: '/api/list/carers/me' })

      const ability = resAbility?.data

      const abilityInstance = createMongoAbility(ability?.rules)

      // eslint-disable-next-line object-shorthand
      dispatch(GlobalActions.setAbility(abilityInstance))
      dispatch(GlobalActions.setPermissionsLoading(false))
    } catch (err) {
      console.error(err)
      dispatch(GlobalActions.setPermissionsLoading(false))
    }
  }
}
